import { useState, useEffect, useRef } from "react";
import api, { BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Camera, Save, User, ImagePlus, Crop, Phone, Building2, Mail, Globe, MapPin } from "lucide-react";
import { toast } from "sonner";
import AvatarCropEditor from "@/components/AvatarCropEditor";
import ImageCropEditor from "@/components/ImageCropEditor";

export default function ProfileEditor() {
  const [profile, setProfile] = useState(null);
  const [username, setUsername] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [emailPublic, setEmailPublic] = useState("");
  const [website, setWebsite] = useState("");
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);
  const [heroCropOpen, setHeroCropOpen] = useState(false);
  const [croppingSaving, setCroppingSaving] = useState(false);
  const profileImgRef = useRef(null);
  const coverImgRef = useRef(null);
  const heroImgRef = useRef(null);

  useEffect(() => {
    api.get("/profile").then((res) => {
      if (res.data) {
        setProfile(res.data);
        setUsername(res.data.username || "");
        setTitle(res.data.title || "");
        setDescription(res.data.description || "");
        setPhone(res.data.phone || "");
        setCompany(res.data.company || "");
        setEmailPublic(res.data.email_public || "");
        setWebsite(res.data.website || "");
        setAddress(res.data.address || "");
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!username.trim()) { toast.error("Username is required"); return; }
    setSaving(true);
    try {
      const res = await api.post("/profile", { username: username.trim(), title, description, phone, company, email_public: emailPublic, website, address });
      setProfile(res.data);
      toast.success(profile ? "Profile updated!" : "Profile created!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save profile");
    } finally { setSaving(false); }
  };

  const handleUpload = async (type, file) => {
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await api.post(`/upload/${type}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (type === 'profile_image') {
        setProfile((prev) => ({
          ...prev,
          profile_image: res.data.url,
          profile_image_original: res.data.url,
          profile_image_crop: null,
        }));
        toast.success("Image uploaded! Adjust your crop.");
        setCropOpen(true);
      } else if (type === 'hero_media') {
        setProfile((prev) => ({
          ...prev,
          hero_media: res.data.url,
          hero_media_original: res.data.url,
          hero_media_crop: null,
        }));
        toast.success("Hero image uploaded! Adjust your crop.");
        setHeroCropOpen(true);
      } else {
        setProfile((prev) => ({ ...prev, [type]: res.data.url }));
        toast.success("Image uploaded!");
      }
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const handleAvatarCropSave = async (cropData) => {
    setCroppingSaving(true);
    try {
      const res = await api.post("/profile/crop-avatar", cropData);
      setProfile((prev) => ({
        ...prev,
        profile_image: res.data.url,
        profile_image_crop: res.data.crop,
      }));
      setCropOpen(false);
      toast.success("Profile picture adjusted!");
    } catch {
      toast.error("Failed to save crop");
    } finally { setCroppingSaving(false); }
  };

  const handleHeroCropSave = async (cropData) => {
    setCroppingSaving(true);
    try {
      const res = await api.post("/profile/crop-hero", cropData);
      setProfile((prev) => ({
        ...prev,
        hero_media: res.data.url,
        hero_media_crop: res.data.crop,
      }));
      setHeroCropOpen(false);
      toast.success("Hero image adjusted!");
    } catch {
      toast.error("Failed to save hero crop");
    } finally { setCroppingSaving(false); }
  };

  const imgUrl = (path) => path ? (path.startsWith('http') ? path : `${BACKEND_URL}${path}`) : null;

  const originalAvatarUrl = imgUrl(profile?.profile_image_original || profile?.profile_image);
  const originalHeroUrl = imgUrl(profile?.hero_media_original || profile?.hero_media);
  const hasProfileImage = !!(profile?.profile_image || profile?.profile_image_original);
  const hasHeroMedia = !!(profile?.hero_media || profile?.hero_media_original);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-8 max-w-2xl">
      <div>
        <h1 className="font-heading text-2xl font-bold mb-1">Profile</h1>
        <p className="text-muted-foreground text-sm">
          {profile ? "Edit your public profile" : "Create your Smart-QR profile"}
        </p>
      </div>

      {profile && (
        <Card className="overflow-hidden">
          <div className="relative h-32 bg-secondary">
            {imgUrl(profile.cover_image) && (
              <img src={imgUrl(profile.cover_image)} alt="Cover" className="w-full h-full object-cover" />
            )}
            <button
              onClick={() => coverImgRef.current?.click()}
              className="absolute bottom-2 right-2 p-2 rounded-lg bg-background/80 backdrop-blur-sm border border-border hover:bg-background transition-colors"
              disabled={uploading}
              data-testid="upload-cover-btn"
            >
              <Camera size={16} />
            </button>
            <input ref={coverImgRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload("cover_image", e.target.files[0])} />
          </div>
          <div className="px-6 pb-6 -mt-10 relative">
            <div className="relative inline-block group">
              <Avatar className="h-20 w-20 border-4 border-background">
                <AvatarImage src={imgUrl(profile.profile_image)} />
                <AvatarFallback className="bg-secondary text-2xl"><User size={32} /></AvatarFallback>
              </Avatar>
              <div className="absolute -bottom-1 -right-1 flex gap-0.5">
                <button
                  onClick={() => profileImgRef.current?.click()}
                  className="p-1.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors shadow-md"
                  disabled={uploading}
                  data-testid="upload-profile-btn"
                >
                  <Camera size={11} />
                </button>
                {hasProfileImage && (
                  <button
                    onClick={() => setCropOpen(true)}
                    className="p-1.5 rounded-full bg-secondary text-foreground border border-border hover:bg-secondary/80 transition-colors shadow-md"
                    data-testid="adjust-profile-btn"
                  >
                    <Crop size={11} />
                  </button>
                )}
              </div>
              <input ref={profileImgRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload("profile_image", e.target.files[0])} />
            </div>
            {hasProfileImage && (
              <p className="text-[10px] text-muted-foreground mt-2">
                Click <Crop size={9} className="inline" /> to adjust crop &amp; zoom
              </p>
            )}
          </div>
        </Card>
      )}

      {/* Hero Media Section */}
      {profile && (
        <Card data-testid="hero-media-card">
          <CardHeader>
            <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
              <ImagePlus size={18} className="text-primary" />
              Hero / Intro Media
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Upload a separate image for the public page hero section (16:9 crop).
            </p>
          </CardHeader>
          <CardContent>
            {imgUrl(profile.hero_media) ? (
              <div className="relative rounded-xl overflow-hidden border border-border">
                <img src={imgUrl(profile.hero_media)} alt="Hero" className="w-full h-48 object-cover" data-testid="hero-media-preview" />
                <div className="absolute bottom-3 right-3 flex gap-2">
                  {hasHeroMedia && (
                    <button
                      onClick={() => setHeroCropOpen(true)}
                      className="p-2 rounded-lg bg-background/80 backdrop-blur-sm border border-border hover:bg-background transition-colors"
                      data-testid="adjust-hero-btn"
                    >
                      <Crop size={16} />
                    </button>
                  )}
                  <button
                    onClick={() => heroImgRef.current?.click()}
                    className="p-2 rounded-lg bg-background/80 backdrop-blur-sm border border-border hover:bg-background transition-colors"
                    disabled={uploading}
                    data-testid="change-hero-btn"
                  >
                    <Camera size={16} />
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => heroImgRef.current?.click()}
                className="w-full h-36 rounded-xl border-2 border-dashed border-border hover:border-primary/40 bg-secondary/30 flex flex-col items-center justify-center gap-2 transition-colors"
                disabled={uploading}
                data-testid="upload-hero-btn"
              >
                <ImagePlus size={24} className="text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Upload hero image</span>
              </button>
            )}
            {hasHeroMedia && (
              <p className="text-[10px] text-muted-foreground mt-2">
                Click <Crop size={9} className="inline" /> to adjust hero crop &amp; zoom
              </p>
            )}
            <input ref={heroImgRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload("hero_media", e.target.files[0])} />
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-base font-semibold">Profile Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSave} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground shrink-0">/p/</span>
                <Input id="username" value={username} onChange={(e) => setUsername(e.target.value.replace(/[^a-zA-Z0-9_-]/g, ""))} placeholder="yourname" required data-testid="profile-username-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="title">Display Name</Label>
              <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="John Smith" data-testid="profile-title-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Bio</Label>
              <Textarea id="description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Tell people about yourself..." rows={3} data-testid="profile-description-input" />
            </div>

            {/* Contact Information */}
            <div className="pt-4 border-t border-border">
              <h3 className="text-sm font-semibold flex items-center gap-2 mb-4">
                <Phone size={15} className="text-primary" />
                Contact Information
              </h3>
              <p className="text-xs text-muted-foreground mb-4">
                Used for vCard download. Visitors can save your contact info directly to their phone.
              </p>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="flex items-center gap-1.5 text-xs">
                      <Phone size={12} className="text-muted-foreground" /> Phone
                    </Label>
                    <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1-555-0123" data-testid="profile-phone-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company" className="flex items-center gap-1.5 text-xs">
                      <Building2 size={12} className="text-muted-foreground" /> Company
                    </Label>
                    <Input id="company" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Acme Corp" data-testid="profile-company-input" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email_public" className="flex items-center gap-1.5 text-xs">
                      <Mail size={12} className="text-muted-foreground" /> Public Email
                    </Label>
                    <Input id="email_public" type="email" value={emailPublic} onChange={(e) => setEmailPublic(e.target.value)} placeholder="hello@example.com" data-testid="profile-email-public-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="website" className="flex items-center gap-1.5 text-xs">
                      <Globe size={12} className="text-muted-foreground" /> Website
                    </Label>
                    <Input id="website" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://example.com" data-testid="profile-website-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address" className="flex items-center gap-1.5 text-xs">
                    <MapPin size={12} className="text-muted-foreground" /> Address
                  </Label>
                  <Input id="address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="123 Main St, City, State" data-testid="profile-address-input" />
                </div>
              </div>
            </div>

            <Button type="submit" disabled={saving} className="btn-glow" data-testid="profile-save-btn">
              <Save size={16} className="mr-2" />
              {saving ? "Saving..." : profile ? "Update Profile" : "Create Profile"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Avatar Crop Editor */}
      {hasProfileImage && (
        <AvatarCropEditor
          open={cropOpen}
          onClose={() => setCropOpen(false)}
          imageUrl={originalAvatarUrl}
          initialCrop={profile?.profile_image_crop}
          onSave={handleAvatarCropSave}
          saving={croppingSaving}
        />
      )}

      {/* Hero Media Crop Editor */}
      {hasHeroMedia && (
        <ImageCropEditor
          open={heroCropOpen}
          onClose={() => setHeroCropOpen(false)}
          imageUrl={originalHeroUrl}
          initialCrop={profile?.hero_media_crop}
          onSave={handleHeroCropSave}
          saving={croppingSaving}
          title="Adjust Hero Image"
          aspect={16 / 9}
          cropShape="rect"
          containerClass="aspect-video"
          hint="Drag to reposition. Use the slider or pinch to zoom. 16:9 crop for hero display."
        />
      )}
    </div>
  );
}
